import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import usePurchaseAdvice from '../../hooks/usePurchaseAdvice';

const PurchaseAdviceEditModal = ({ isOpen, onClose, adviceId, onUpdateSuccess }) => {
    const { t } = useTranslation();
    const { getPurchaseAdviceById, updatePurchaseAdvice } = usePurchaseAdvice();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        advice_date: '',
        payment_terms: '',
        supplier_invoice_number: '',
        supplier_invoice_date: '',
        present_shipping: 0,
        remark: '',
        internal_remark: '',
        items_update: [],
    });
    const [error, setError] = useState(null);

    const activeRequestRef = useRef({ id: null, promise: null });

    useEffect(() => {
        if (!isOpen || !adviceId) return;

        let mounted = true;

        const fetchAdviceDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                let promise;
                if (activeRequestRef.current.id === adviceId && activeRequestRef.current.promise) {
                    promise = activeRequestRef.current.promise;
                } else {
                    promise = getPurchaseAdviceById(adviceId);
                    activeRequestRef.current = { id: adviceId, promise };
                }

                const result = await promise;
                if (!mounted) return;

                if (result.success && result.data) {
                    const data = result.data;
                    
                    // Format dates for HTML date input (YYYY-MM-DD)
                    const formatDateForInput = (dateStr) => {
                        if (!dateStr) return '';
                        try {
                            return dateStr.split('T')[0];
                        } catch (e) {
                            return '';
                        }
                    };

                    setFormData({
                        advice_date: formatDateForInput(data.advice_date),
                        payment_terms: data.payment_terms || '',
                        supplier_invoice_number: data.supplier_invoice_number || '',
                        supplier_invoice_date: formatDateForInput(data.supplier_invoice_date),
                        present_shipping: data.present_shipping || 0,
                        remark: data.remark || '',
                        internal_remark: data.internal_remark || '',
                        items_update: (data.items || []).map(item => ({
                            id: item.id,
                            quantity: item.quantity || 0,
                            price: item.price || 0,
                            remark: item.remark || '',
                            product_name: item.product_name || item.item_name || 'Unknown Product',
                            size_name: item.size_name || '',
                        })),
                    });
                } else {
                    setError(result.error || t('purchaseAdvice.edit.error'));
                }
            } catch (err) {
                if (mounted) setError(err.message || t('purchaseAdvice.edit.error'));
            } finally {
                if (mounted) setLoading(false);
                // Clear the promise reference once complete so future opens refetch
                if (activeRequestRef.current.id === adviceId) {
                    activeRequestRef.current = { id: null, promise: null };
                }
            }
        };

        fetchAdviceDetails();

        return () => {
            mounted = false;
        };
    }, [isOpen, adviceId, getPurchaseAdviceById, t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setFormData(prev => {
            const newItems = [...prev.items_update];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prev, items_update: newItems };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        const payload = {
            advice_date: formData.advice_date,
            payment_terms: formData.payment_terms,
            supplier_invoice_number: formData.supplier_invoice_number,
            supplier_invoice_date: formData.supplier_invoice_date,
            present_shipping: Number(formData.present_shipping),
            remark: formData.remark,
            internal_remark: formData.internal_remark || '',
            items_update: formData.items_update.map(item => ({
                id: item.id,
                quantity: Number(item.quantity),
                price: Number(item.price),
                remark: item.remark,
            })),
        };

        try {
            const result = await updatePurchaseAdvice(adviceId, payload);
            if (result.success) {
                onUpdateSuccess();
                onClose();
            } else {
                setError(result.error || t('purchaseAdvice.edit.error'));
            }
        } catch (err) {
            setError(err.message || t('purchaseAdvice.edit.error'));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseAdvice.edit.header.title')}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BRAND.PRIMARY }} />
                            <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.loading')}</p>
                        </div>
                    ) : error ? (
                        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-600">
                            {error}
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* General Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.adviceDate')}</label>
                                    <input
                                        type="date"
                                        name="advice_date"
                                        value={formData.advice_date}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.paymentTerms')}</label>
                                    <input
                                        type="text"
                                        name="payment_terms"
                                        value={formData.payment_terms}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.supplierInvoiceNumber')}</label>
                                    <input
                                        type="text"
                                        name="supplier_invoice_number"
                                        value={formData.supplier_invoice_number}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.supplierInvoiceDate')}</label>
                                    <input
                                        type="date"
                                        name="supplier_invoice_date"
                                        value={formData.supplier_invoice_date}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.presentShipping')}</label>
                                    <input
                                        type="number"
                                        name="present_shipping"
                                        value={formData.present_shipping}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseAdvice.edit.fields.remark')}</label>
                                    <input
                                        type="text"
                                        name="remark"
                                        value={formData.remark}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500"
                                        style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                                <textarea
                                    name="internal_remark"
                                    value={formData.internal_remark}
                                    onChange={handleChange}
                                    placeholder={t('common.formFields.placeholders.internalRemarksNotVisibleSupplier')}
                                    className="w-full px-4 py-2 rounded-xl border-2 focus:outline-none focus:border-blue-500 resize-none"
                                    rows="2"
                                    style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: '#f0fdf4' }}
                                />
                            </div>

                            {/* Items Table */}
                            <div>
                                <h3 className="text-lg font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseAdvice.edit.fields.items')}</h3>
                                <div className="overflow-x-auto rounded-xl border" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr style={{ backgroundColor: UI_COLORS.BACKGROUNDS.PAGE }}>
                                                <th className="p-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.fields.product')}</th>
                                                <th className="p-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.fields.size')}</th>
                                                <th className="p-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.fields.quantity')}</th>
                                                <th className="p-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.fields.price')}</th>
                                                <th className="p-3 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseAdvice.edit.fields.remark')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.items_update.map((item, index) => (
                                                <tr key={item.id} className="border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                    <td className="p-3 text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{item.product_name || '-'}</td>
                                                    <td className="p-3 text-sm" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{item.size_name || '-'}</td>
                                                    <td className="p-3">
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            className="w-24 px-2 py-1 rounded border focus:outline-none focus:border-blue-500"
                                                            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <input
                                                            type="number"
                                                            value={item.price}
                                                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                                            className="w-24 px-2 py-1 rounded border focus:outline-none focus:border-blue-500"
                                                            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <input
                                                            type="text"
                                                            value={item.remark}
                                                            onChange={(e) => handleItemChange(index, 'remark', e.target.value)}
                                                            className="w-full px-2 py-1 rounded border focus:outline-none focus:border-blue-500"
                                                            style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-xl font-semibold transition-colors hover:bg-gray-100"
                        style={{ color: UI_COLORS.TEXT.SECONDARY, border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}` }}
                        disabled={submitting}
                    >
                        {t('purchaseAdvice.edit.buttons.cancel')}
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-6 py-2 rounded-xl font-semibold text-white transition-opacity hover:opacity-90 flex items-center gap-2"
                        style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY }}
                        disabled={submitting || loading}
                    >
                        {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                        {t('purchaseAdvice.edit.buttons.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseAdviceEditModal;
